import React, { useState, useEffect, useCallback, useRef } from 'react';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import { API_BASE_URL } from '../config';
import Alert from './Alert';
import Button from './Button';
import CustomerSelect from './CustomerSelect';
import './AgreementGenerator.css';

type FormType = 'agreement' | 'upsell';
type UpsellType = 'cameras' | 'rate' | 'term';

interface Customer {
  customer_tag: string;
  description: string;
  stripe: {
    name: string;
    email: string;
    phone: string;
    subscription_status: string | null;
    contract_units: number | null;
    address_line1: string;
    address_line2: string | null;
    address_city: string;
    address_state: string;
    address_postal_code: string;
  };
}

interface FormData {
  customer_name: string;
  customer_contact: string;
  customer_address: string;
  customer_phone: string;
  customer_email: string;
  number_of_body_cameras: string;
  fees: string;
  initial_term_months: string;
  payment_schedule: string;
  package_tier: 'patrol' | 'tactical' | 'command';
  implementation_fee_pct: string;
  ae_name: string;
  ae_email: string;
}

const PACKAGES = [
  // { value: 'patrol', label: 'Patrol' },
  // { value: 'tactical', label: 'Tactical' },
  { value: 'command', label: 'Command' },
] as const;

// Command package features — synced from spreadsheet 111mdF_iHiY8BTMFCeuU1vqOUNMgcOpgbc5v7rZHaqbY
const PACKAGE_FEATURES = [
  { feature: 'GPS', command: 'Real-time' },
  { feature: 'Shift Dashboard', command: 'Yes' },
  { feature: 'Geofencing', command: 'Unlimited' },
  { feature: 'Mobile App', command: 'Yes' },
  { feature: 'Safety Inbox', command: 'Yes' },
  { feature: 'Redaction', command: 'Yes' },
  { feature: 'Report Agent', command: 'Unlimited' },
  { feature: 'Workforce Intelligence', command: 'Yes' },
  { feature: 'Alerts', command: 'Video Alerts' },
  { feature: 'Upload', command: 'Priority videos' },
  { feature: 'Pooled Livestream', command: '' }, // per deal, see livestreamAllowance
  { feature: 'Access Control', command: 'Admin, Personnel, External' },
  { feature: 'Video Retention', command: '30 days' },
  { feature: 'Archiving', command: 'Unlimited' },
  { feature: 'Warranty & Support', command: 'Full' },
  { feature: 'Mounting Hardware', command: 'Magnet, alligator & shoulder clip, charger, single docking stations' },
];

const PAYMENT_SCHEDULES = [
  { value: 'annual', label: 'Annual', shortLabel: 'Annual' },
  { value: 'upfront', label: 'Upfront', shortLabel: 'Upfront' },
  { value: 'financing', label: 'Payment Agreement', shortLabel: 'Payment Agreement' },
];

const DEFAULT_TERM_DISCOUNTS: Record<string, number> = {
  '12': 0,
  '36': 0,
  '60': 0,
};

// The rows in the Term Discounts editor. 1 Yr is editable like the others: it is
// the list term, so it stays at 0 unless someone deliberately discounts it, but
// there is no reason a rep cannot give ground on a one-year deal.
const TERM_DISCOUNT_ROWS = [
  { key: '12', label: '1 Yr' },
  { key: '36', label: '3 Yr' },
  { key: '60', label: '5 Yr' },
];

const SCHEDULE_ORDER = ['annual', 'upfront', 'financing'];

/**
 * The $/unit/mo the customer actually pays on a given proposal -- the number
 * the Term Commitment and Payment Options tables show for the selected row.
 *
 * The agreement doc stamps this same figure (`_effective_rate` in
 * agreement_service.py mirrors the operand order and the ceil-to-cent), so a
 * generated contract can never quote a different rate than the quote it came
 * from. That drift was the bug: the doc printed the rep's list rate on the
 * rental-fee line while every total below it was term-discounted.
 */
export function effectivePerUnit(tcv: number, units: number, months: number) {
  if (!units || !months) return 0;
  return Math.ceil(tcv / units / months * 100) / 100;
}

/**
 * Pooled livestream allowance: 5 minutes per camera, shown in minutes under an
 * hour and in whole hours (rounded down) from there. Mirrors
 * `_livestream_allowance` in agreement_service.py, which stamps `$livestream`.
 */
export function livestreamAllowance(cameras: number) {
  const minutes = Math.max(Math.floor(cameras) || 0, 0) * 5;
  if (minutes < 60) return `${minutes} minutes`;
  const hours = Math.floor(minutes / 60);
  return `${hours} hour${hours === 1 ? '' : 's'}`;
}

/** Dollars, with cents only when there are any. Mirrors `_money` in agreement_service.py. */
export function formatMoney(amount: number) {
  const cents = Math.round(amount * 100);
  const dp = cents % 100 === 0 ? 0 : 2;
  return (cents / 100).toLocaleString('en-US', { minimumFractionDigits: dp, maximumFractionDigits: dp });
}

export function computeDeal(
  units: number, rate: number, months: number, schedule: string,
  _baselineSchedule?: string, baselineTerm?: string, implFeePct: number = 20,
  termDiscounts: Record<string, number> = DEFAULT_TERM_DISCOUNTS,
  upfrontDiscountPct: number = 0.10,
  annualDiscountPct: number = 0,
) {
  const baseTcv = Math.ceil(units * rate * months);

  const termKey = String(months);
  // A term earns no commitment discount when it is the term the customer is
  // already on -- that is an upsell fact, so it needs an explicit baseline.
  // Passing none means nothing is exempt; it used to mean everything was.
  const isTermBaseline = !!baselineTerm && termKey === baselineTerm;
  const termDiscountPct = isTermBaseline ? 0 : (termDiscounts[termKey] || 0);

  const termDiscount = Math.ceil(baseTcv * termDiscountPct);
  const tcvAfterTermDiscount = baseTcv - termDiscount;

  const isUpfront = schedule === 'upfront';
  const isAnnual = schedule === 'annual';
  const isFinancing = schedule === 'financing';

  // Only the TERM commitment moves the price of a camera. A pay-timing
  // discount buys a cheaper TOTAL -- it is bought with cash flow, not with a
  // longer commitment -- so the $/unit/mo is struck here, off the
  // term-discounted TCV, and the upfront/annual cut comes off the total below.
  //
  // Folding it into the rate instead was the bug: picking Upfront silently
  // repriced the camera, so the same 60-month deal quoted $50.25/device/mo on
  // the Annual row and $45.23 on the Upfront row, and the rate the contract
  // stamped (and every renewal priced off it) moved with the payment method.
  //
  // The rate is rounded once to the cent, and the monthly / ACV / onboarding
  // are derived FROM it rather than computed alongside it, so `rate x units`
  // reconciles wherever they are shown -- and against the agreement, whose
  // `_effective_rate` / `_money` mirror this exactly. Onboarding used to be
  // pinned to the LIST ACV, which is how a contract came to claim "$1,440
  // (20% of ACV)" on a page whose ACV was $4,824.
  const effectiveRate = effectivePerUnit(tcvAfterTermDiscount, units, months);
  const monthly = effectiveRate * units;
  const acv = monthly * 12;
  const onboarding = Math.ceil(acv * (implFeePct / 100) * 100) / 100;

  // The term billed at the rate above, before the pay-timing cut. Discounts
  // still stack sequentially: the pay cut comes off this number, never off list.
  const rateTcv = monthly * months;
  const payDiscountPct = isUpfront ? upfrontDiscountPct : isAnnual ? annualDiscountPct : 0;
  const payDiscount = Math.ceil(rateTcv * payDiscountPct);
  const tcv = rateTcv - payDiscount;
  const combinedDiscountPct = termDiscountPct + payDiscountPct;
  const discount = baseTcv - tcv;

  let paymentAmount: number;
  let paymentLabel: string;
  if (isFinancing) {
    paymentAmount = monthly;
    paymentLabel = `$${formatMoney(monthly)}/mo`;
  } else if (isAnnual) {
    // An annual prepay cut is spread across the invoices it buys, so
    // years x invoice still adds up to the TCV printed beside it.
    const years = months / 12;
    paymentAmount = years > 0 ? Math.round(tcv / years * 100) / 100 : tcv;
    paymentLabel = `$${formatMoney(paymentAmount)}/yr`;
  } else {
    paymentAmount = tcv;
    paymentLabel = `$${formatMoney(tcv)}`;
  }

  return {
    monthly, acv, baseTcv, discount, effectiveRate,
    payDiscountPct, termDiscountPct, combinedDiscountPct,
    rateTcv, payDiscount,
    tcv, onboarding, paymentAmount, paymentLabel, nextTiers: [],
    isFinancing, isAnnual, isUpfront,
  };
}

/**
 * Whole-percent input.
 *
 * These were `<input type="number">` bound to a rounded number. A field sitting
 * at its 0 default puts the caret before the zero, so typing "12" produced
 * "012" -- and React leaves that in the DOM, because for number inputs it
 * compares the node against the prop loosely ("012" == 12) precisely so it does
 * not clobber half-typed values like "1.". The stored percentage was correct;
 * the field just read wrong.
 *
 * text + inputMode="numeric" restores a strict string comparison, so the
 * normalized value always wins. The local draft lets the box be empty while you
 * retype it -- without it, clearing the field snaps back to "0" and the next
 * keystroke rebuilds "012" -- and blur settles it back to the stored number,
 * which is what leaves a 0 showing only when nothing is there.
 */
export const PercentInput: React.FC<{
  value: number;
  onChange: (pct: number) => void;
  label: string;
  style?: React.CSSProperties;
}> = ({ value, onChange, label, style }) => {
  const [draft, setDraft] = useState<string | null>(null);
  return (
    <input
      type="text"
      inputMode="numeric"
      aria-label={label}
      value={draft ?? String(Math.round(value))}
      onChange={(e) => {
        const digits = e.target.value.replace(/\D/g, '').replace(/^0+(?=\d)/, '');
        const next = digits === '' ? '' : String(Math.min(100, parseInt(digits, 10)));
        setDraft(next);
        onChange(next === '' ? 0 : parseInt(next, 10));
      }}
      onBlur={() => setDraft(null)}
      className="term-discount-input"
      style={style}
    />
  );
};

const AgreementGenerator: React.FC = () => {
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [selectedCustomer, setSelectedCustomer] = useState<string>('');
  const [isCustomCustomer, setIsCustomCustomer] = useState(false);
  const [formData, setFormData] = useState<FormData>({
    customer_name: '',
    customer_contact: '',
    customer_address: '',
    customer_phone: '',
    customer_email: '',
    number_of_body_cameras: '',
    fees: '',
    initial_term_months: '12',
    payment_schedule: 'upfront',
    package_tier: 'command',
    implementation_fee_pct: '20',
    ae_name: '',
    ae_email: ''
  });
  const [loading, setLoading] = useState(false);
  const [formType, setFormType] = useState<FormType>('agreement');
  const [error, setError] = useState<string | null>(null);
  const [docUrl, setDocUrl] = useState<string | null>(null);
  const [celebrating, setCelebrating] = useState(false);
  const [aeEmailManuallyEdited, setAeEmailManuallyEdited] = useState(false);
  const [includeTrialClause, setIncludeTrialClause] = useState(false);
  // Wi-Fi only: customer brings their own SIMs and accepts that livestream +
  // in-field upload are disabled. Price stays at the quoted rate — this only
  // changes the contract LANGUAGE (stamps the SIM-card clause into the doc).
  const [wifiOnly, setWifiOnly] = useState(false);
  // Buy out: Plix credits the remainder of the prospect's prior vendor
  // contract. Replaces the doc's buyout_clause placeholder.
  const [buyOut, setBuyOut] = useState(false);
  const [presentationMode, setPresentationMode] = useState(false);
  const [presentationBaseline, setPresentationBaseline] = useState('');
  const [presentationTermBaseline, setPresentationTermBaseline] = useState('12');
  const [billingContext, setBillingContext] = useState<{ interval: string; interval_count: number; raw_rate: number } | null>(null);
  const [upsellTypes, setUpsellTypes] = useState<Set<UpsellType>>(() => new Set<UpsellType>(['cameras']));
  const [currentSub, setCurrentSub] = useState<{ units: number; rate: number } | null>(null);
  const [existingTerm, setExistingTerm] = useState('12');
  const [termDiscounts, setTermDiscounts] = useState<Record<string, number>>({ ...DEFAULT_TERM_DISCOUNTS });
  const [upfrontDiscount, setUpfrontDiscount] = useState(0.10);
  const [annualDiscount, setAnnualDiscount] = useState(0);
  const [rateDiscountPct, setRateDiscountPct] = useState(0);
  const [visiblePayOptions, setVisiblePayOptions] = useState<Set<string>>(() => new Set(['upfront', 'financing']));
  const [visibleTermOptions, setVisibleTermOptions] = useState<Set<string>>(() => new Set(['12', '36', '60']));
  const pricingRef = useRef<HTMLDivElement>(null);

  const hasUpsell = (t: UpsellType) => upsellTypes.has(t);
  const toggleUpsell = (t: UpsellType) => {
    setUpsellTypes(prev => {
      const next = new Set<UpsellType>(prev);
      if (next.has(t)) { next.delete(t); } else { next.add(t); }
      if (next.size === 0) next.add('cameras');
      return next;
    });
  };

  const togglePresentationMode = useCallback(() => {
    const next = !presentationMode;
    setPresentationMode(next);
    if (next) {
      // Anchor baselines to the LEAST advantageous visible option so that
      // savings still render against a meaningful comparison. Pinning to the
      // current selection would make e.g. Upfront/5y its own baseline and
      // zero out the savings banner the moment we go live.
      const payBaseline = SCHEDULE_ORDER.find(s => visiblePayOptions.has(s)) || formData.payment_schedule;
      const termBaseline = Array.from(visibleTermOptions).sort(
        (a, b) => parseInt(a) - parseInt(b),
      )[0] || formData.initial_term_months;
      setPresentationBaseline(payBaseline);
      setPresentationTermBaseline(termBaseline);
    }
    document.documentElement.classList.toggle('presentation-mode', next);
  }, [presentationMode, formData.payment_schedule, formData.initial_term_months, visiblePayOptions, visibleTermOptions]);

  // Clean up on unmount
  useEffect(() => {
    return () => document.documentElement.classList.remove('presentation-mode');
  }, []);

  useEffect(() => {
    const fetchCustomers = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/api/customers`);
        if (!response.ok) throw new Error('Failed to fetch customers');
        const data = await response.json();
        setCustomers(data.customers);
      } catch (err) {
        setError('Failed to load customers');
      }
    };
    fetchCustomers();
  }, []);

  useEffect(() => {
    if (selectedCustomer) {
      const customer = customers.find(c => c.customer_tag === selectedCustomer);
      if (customer) {
        const address = [
          customer.stripe.address_line1, customer.stripe.address_line2,
          customer.stripe.address_city, customer.stripe.address_state,
          customer.stripe.address_postal_code
        ].filter(Boolean).join(', ');
        setFormData(prev => ({
          ...prev,
          customer_name: customer.description || customer.customer_tag || '',
          customer_contact: '',
          customer_email: customer.stripe.email || '',
          customer_phone: customer.stripe.phone || '',
          customer_address: address
        }));

        // For upsells, fetch the current subscription rate from Stripe
        if (formType === 'upsell') {
          fetch(`${API_BASE_URL}/api/customer/${encodeURIComponent(selectedCustomer)}/subscription-rate`)
            .then(r => r.ok ? r.json() : null)
            .then(data => {
              if (data?.rate_per_unit) {
                const subUnits = data.quantity || customer.stripe.contract_units || 0;
                setFormData(prev => ({
                  ...prev,
                  fees: String(data.rate_per_unit),
                  number_of_body_cameras: hasUpsell('cameras') ? prev.number_of_body_cameras : String(subUnits),
                }));
                setBillingContext({
                  interval: data.interval,
                  interval_count: data.interval_count || 1,
                  raw_rate: data.rate_per_unit_raw,
                });
                setCurrentSub({ units: subUnits, rate: data.rate_per_unit });
              }
            })
            .catch(() => {});
        } else {
          setBillingContext(null);
          setCurrentSub(null);
        }
      }
    }
  }, [selectedCustomer, customers, formType]);

  // When upsell types change, update camera count from Stripe if cameras isn't selected
  useEffect(() => {
    if (formType === 'upsell' && currentSub && !hasUpsell('cameras')) {
      setFormData(prev => ({ ...prev, number_of_body_cameras: String(currentSub.units) }));
    }
  }, [upsellTypes, formType, currentSub]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    if (name === 'ae_name' && !aeEmailManuallyEdited) {
      const firstName = value.trim().split(/\s+/)[0].toLowerCase();
      setFormData(prev => ({ ...prev, ae_name: value, ae_email: firstName ? `${firstName}@plix.ai` : '' }));
      return;
    }
    if (name === 'ae_email') setAeEmailManuallyEdited(true);
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const triggerCelebration = useCallback(() => {
    setCelebrating(true);
    setTimeout(() => setCelebrating(false), 3000);
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setDocUrl(null);
    try {
      const endpoint = formType === 'upsell'
        ? `${API_BASE_URL}/api/agreements/generate-upsell`
        : `${API_BASE_URL}/api/agreements/generate`;

      const payload = formType === 'upsell'
        ? {
            customer_name: formData.customer_name,
            customer_contact: formData.customer_contact || formData.customer_name,
            customer_address: formData.customer_address,
            customer_phone: formData.customer_phone,
            customer_email: formData.customer_email,
            ae_name: formData.ae_name,
            ae_email: formData.ae_email,
            number_of_body_cameras: hasUpsell('term') ? String(currentSub?.units || 0) : formData.number_of_body_cameras,
            fees: Math.ceil(parseFloat(formData.fees) || 0),
            payment_schedule: formData.payment_schedule,
            upsell_type: Array.from(upsellTypes).join('+'),
            initial_term_months: parseInt(formData.initial_term_months) || 12,
            current_units: currentSub?.units || 0,
            current_rate: currentSub?.rate || 0,
          }
        : { ...formData, include_trial: includeTrialClause,
            wifi_only: wifiOnly, buy_out: buyOut,
            // The doc must print the same numbers as the on-screen quote, so
            // send the discount rates the quote actually used.
            term_discount_pct: deal?.termDiscountPct ?? 0,
            pay_discount_pct: deal?.payDiscountPct ?? 0 };

      const response = await fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || `Failed to generate ${formType === 'upsell' ? 'change order' : 'agreement'}`);
      }
      const data = await response.json();
      setDocUrl(data.doc_url);
      triggerCelebration();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setLoading(false);
    }
  };

  const rawUnits = parseInt(formData.number_of_body_cameras) || 0;
  const rate = parseFloat(formData.fees) || 0;
  const months = parseInt(formData.initial_term_months) || 12;

  // Compute effective units for pricing:
  // - cameras only: additional units at rate
  // - rate only or term only: current subscription units
  // - cameras + rate/term: additional units (new plan total = currentSub.units + rawUnits for display)
  const isUpsell = formType === 'upsell';
  const onlyCameras = isUpsell && hasUpsell('cameras') && !hasUpsell('rate') && !hasUpsell('term');
  const onlyTermOrRate = isUpsell && !hasUpsell('cameras') && (hasUpsell('term') || hasUpsell('rate'));
  const units = onlyTermOrRate ? (currentSub?.units || rawUnits) : rawUnits;
  const effectiveRate = isUpsell && hasUpsell('rate') && currentSub
    ? Math.ceil(currentSub.rate * (1 - rateDiscountPct / 100))
    : isUpsell ? (currentSub?.rate || rate) : rate;
  const hasDeal = isUpsell
    ? (hasUpsell('cameras') ? units > 0 : (currentSub?.units || 0) > 0) && (hasUpsell('rate') ? rate > 0 : (currentSub?.rate || 0) > 0)
    : units > 0 && rate > 0;
  const baseline = formData.payment_schedule;
  const implFee = parseInt(formData.implementation_fee_pct) || 20;
  // In presentation mode, lock baselines to what was selected when entering.
  const effectivePayBaseline = presentationMode ? presentationBaseline : baseline;
  // For term extensions, baseline is the existing term so discount shows relative to current.
  // When editing (not in presentation mode), pin the baseline to the shortest enabled
  // term — using the currently-selected term would hide every shorter row in the
  // comparison table the moment the user clicked a longer one.
  const shortestVisibleTerm = Array.from(visibleTermOptions).sort(
    (a, b) => parseInt(a) - parseInt(b),
  )[0] || formData.initial_term_months;
  const effectiveTermBaseline = isUpsell && hasUpsell('term')
    ? existingTerm
    : presentationMode ? presentationTermBaseline : shortestVisibleTerm;
  // Baseline for the HEADLINE deal + Payment Options table. These must mirror the
  // Term Commitment rows, which always measure the discount against `existingTerm`
  // (the 1yr list term for new agreements). `effectiveTermBaseline` is the shortest
  // *visible* term and only exists to drive the comparison table's row visibility;
  // reusing it for the deal silently zeroed the term discount whenever the selected
  // term was the shortest one still shown — e.g. after hiding the 1yr row, or in
  // Share Live (baseline pinned to the shortest visible term). That left the hero
  // monthly/TCV at list price while the table showed the discounted $/unit/mo.
  // Upsell behavior is preserved (relative-to-existing-term discounts).
  // New agreements have no existing term to be exempt from -- `existingTerm`
  // merely defaults to '12' and its control is upsell-only, so leaving it in
  // place silently zeroed any 1 Yr discount. Upsells are unchanged.
  const dealTermBaseline = isUpsell ? effectiveTermBaseline : undefined;
  // The Term Commitment rows must agree with the headline, or the table shows a
  // discounted 1 Yr row while the hero quotes list price.
  const comparisonTermBaseline = isUpsell ? existingTerm : undefined;
  const deal = hasDeal ? computeDeal(units, effectiveRate, months, baseline, effectivePayBaseline, dealTermBaseline, implFee, termDiscounts, upfrontDiscount, annualDiscount) : null;

  // Rate savings: when rate is lower than current, show savings over the term
  const rateSavings = isUpsell && currentSub && hasUpsell('rate') && effectiveRate < currentSub.rate
    ? (currentSub.rate - effectiveRate) * (hasUpsell('cameras') ? currentSub.units + units : currentSub.units) * months
    : 0;
  // Break down discount components for display. Taken off the computed totals
  // rather than re-derived from the percentages, so the two parts still add up
  // to the headline saving once the rate has been rounded to the cent.
  const termSavings = deal ? Math.max(0, deal.baseTcv - deal.rateTcv) : 0;
  const paySavings = deal ? deal.payDiscount : 0;
  const totalSavings = (deal?.discount || 0) + rateSavings;
  const selectedCust = customers.find(c => c.customer_tag === selectedCustomer);

  const [exporting, setExporting] = useState(false);

  const exportToPdf = async () => {
    if (!pricingRef.current || !deal) return;
    setExporting(true);
    try {
      // Hide buttons before capture
      const btns = pricingRef.current.querySelectorAll('.share-live-btn, .export-pdf-btn');
      btns.forEach(b => (b as HTMLElement).style.display = 'none');

      const canvas = await html2canvas(pricingRef.current, {
        scale: 2,
        useCORS: true,
        backgroundColor: '#ffffff',
      });

      // Restore buttons
      btns.forEach(b => (b as HTMLElement).style.display = '');

      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF('p', 'mm', 'letter');
      const pageW = pdf.internal.pageSize.getWidth();
      const pageH = pdf.internal.pageSize.getHeight();
      const margin = 10;
      const availW = pageW - margin * 2;
      const availH = pageH - margin * 2;
      const imgAspect = canvas.width / canvas.height;
      let w = availW;
      let h = w / imgAspect;
      if (h > availH) {
        h = availH;
        w = h * imgAspect;
      }

      pdf.addImage(imgData, 'PNG', margin, margin, w, h);
      pdf.setFontSize(7);
      pdf.setTextColor(160);
      pdf.text(`Plix AI · Confidential · ${new Date().toLocaleDateString()}`, pageW / 2, pageH - 5, { align: 'center' });

      const name = formData.customer_name || 'Customer';
      pdf.save(`Plix Quote ${name} ${new Date().toISOString().slice(0, 10)}.pdf`);
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="agreement-generator">
      {celebrating && (
        <div className="celebration-overlay" onClick={() => setCelebrating(false)}>
          <div className="gong-animation">
            <div className="gong">
              <div className="gong-face"></div>
              <div className="gong-ring ring-1"></div>
              <div className="gong-ring ring-2"></div>
              <div className="gong-ring ring-3"></div>
              <div className="gong-ring ring-4"></div>
              <div className="summit-flag" />
            </div>
            <div className="celebration-text">Summit Reached!</div>
            <div className="confetti-container">
              {Array.from({ length: 30 }).map((_, i) => (
                <div key={i} className="confetti" style={{
                  left: `${Math.random() * 100}%`,
                  animationDelay: `${Math.random() * 0.5}s`,
                  animationDuration: `${1.5 + Math.random() * 1.5}s`,
                  backgroundColor: ['#4f46e5', '#22c55e', '#f59e0b', '#ef4444', '#8b5cf6', '#06b6d4'][i % 6],
                }} />
              ))}
            </div>
          </div>
        </div>
      )}

      <div className="agreement-layout">
        {/* LEFT: Form */}
        <div className="agreement-form-panel">
          <div className="form-type-selector">
            <button
              type="button"
              className={`form-type-option ${formType === 'agreement' ? 'active' : ''}`}
              onClick={() => { setFormType('agreement'); setDocUrl(null); }}
            >
              New Agreement
            </button>
            <button
              type="button"
              className={`form-type-option ${formType === 'upsell' ? 'active' : ''}`}
              onClick={() => { setFormType('upsell'); setDocUrl(null); }}
            >
              Change Order
            </button>
          </div>
          <Alert type="error">{error}</Alert>

          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label htmlFor="customer">Customer</label>
              <div className="customer-toggle">
                <button type="button" className={`toggle-btn ${!isCustomCustomer ? 'active' : ''}`}
                  onClick={() => { setIsCustomCustomer(false); }}>
                  Existing
                </button>
                <button type="button" className={`toggle-btn ${isCustomCustomer ? 'active' : ''}`}
                  onClick={() => {
                    setIsCustomCustomer(true);
                    setSelectedCustomer('');
                    setFormData(prev => ({ ...prev, customer_name: '', customer_email: '', customer_phone: '', customer_address: '' }));
                  }}>
                  Custom
                </button>
              </div>
              {!isCustomCustomer ? (
                <CustomerSelect
                  id="customer"
                  value={selectedCustomer}
                  onChange={setSelectedCustomer}
                  options={customers.map(c => ({ value: c.customer_tag, label: c.description || c.stripe.name }))}
                  placeholder="Search customers..."
                  required
                />
              ) : (
                <input type="text" id="customer_name" name="customer_name" value={formData.customer_name}
                  onChange={handleInputChange} placeholder="Customer / Organization name" required />
              )}
            </div>

            {!isCustomCustomer && selectedCust && (
              <div className="customer-info">
                <span className="customer-info-name">{formData.customer_name}</span>
                {formData.customer_contact && <span>{formData.customer_contact}</span>}
                <span>{formData.customer_email}</span>
                {formData.customer_phone && <span>{formData.customer_phone}</span>}
                {formData.customer_address && <span className="customer-info-address">{formData.customer_address}</span>}
                {formType === 'upsell' && selectedCust.stripe.contract_units && (
                  <span className="customer-info-units">Current: {selectedCust.stripe.contract_units} devices</span>
                )}
              </div>
            )}

            {isCustomCustomer && (
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="customer_email">Email</label>
                  <input type="email" id="customer_email" name="customer_email" value={formData.customer_email}
                    onChange={handleInputChange} placeholder="customer@example.com" />
                </div>
                <div className="form-group">
                  <label htmlFor="customer_phone">Phone</label>
                  <input type="tel" id="customer_phone" name="customer_phone" value={formData.customer_phone}
                    onChange={handleInputChange} placeholder="555-0000" />
                </div>
              </div>
            )}

            {isCustomCustomer && (
              <div className="form-group">
                <label htmlFor="customer_address">Address</label>
                <input type="text" id="customer_address" name="customer_address" value={formData.customer_address}
                  onChange={handleInputChange} placeholder="Street, City, State, ZIP" />
              </div>
            )}

            <div className="form-group">
              <label htmlFor="customer_contact">Contact Name</label>
              <input type="text" id="customer_contact" name="customer_contact" value={formData.customer_contact}
                onChange={handleInputChange} placeholder="Person signing" required />
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="ae_name">Account Executive</label>
                <input type="text" id="ae_name" name="ae_name" value={formData.ae_name}
                  onChange={handleInputChange} placeholder="Name" required />
              </div>
              <div className="form-group">
                <label htmlFor="ae_email">AE Email</label>
                <input type="email" id="ae_email" name="ae_email" value={formData.ae_email}
                  onChange={handleInputChange} placeholder="email@plix.ai" required />
              </div>
            </div>

            {formType === 'agreement' && (
              <div className="form-group">
                <label>Package</label>
                <div className="package-selector">
                  {PACKAGES.map(pkg => (
                    <button
                      key={pkg.value}
                      type="button"
                      className={`package-option ${formData.package_tier === pkg.value ? 'active' : ''}`}
                      onClick={() => setFormData(prev => ({ ...prev, package_tier: pkg.value as FormData['package_tier'] }))}
                    >
                      {pkg.label}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Upsell type selector */}
            {formType === 'upsell' && (
              <div className="form-group">
                <label>Change Type</label>
                <div className="upsell-type-selector">
                  {([
                    { value: 'cameras' as UpsellType, label: 'Add Cameras' },
                    { value: 'rate' as UpsellType, label: 'Rate Change' },
                    { value: 'term' as UpsellType, label: 'Term Extension' },
                  ]).map(opt => (
                    <button
                      key={opt.value}
                      type="button"
                      className={`upsell-type-option ${hasUpsell(opt.value) ? 'active' : ''}`}
                      onClick={() => {
                        toggleUpsell(opt.value);
                        if (opt.value === 'term' && !hasUpsell('term')) {
                          setFormData(prev => ({ ...prev, initial_term_months: '36' }));
                        }
                      }}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Current subscription context */}
            {formType === 'upsell' && currentSub && currentSub.units > 0 && (
              <div className="current-plan-card">
                <div className="current-plan-label">Current Subscription</div>
                <div className="current-plan-metrics">
                  <div><strong>{currentSub.units}</strong> cameras</div>
                  <div><strong>${currentSub.rate}</strong>/device/mo</div>
                  <div><strong>${(currentSub.units * currentSub.rate).toLocaleString()}</strong>/mo total</div>
                </div>
                {billingContext && billingContext.interval !== 'month' && (
                  <div className="current-plan-billing">
                    Billed {billingContext.interval_count > 1 ? `every ${billingContext.interval_count} ` : ''}{billingContext.interval}{billingContext.interval_count > 1 ? 's' : 'ly'} at ${billingContext.raw_rate}/unit
                  </div>
                )}
              </div>
            )}

            {/* Camera count + rate inputs */}
            <div className="form-row">
              {(hasUpsell('cameras') || hasUpsell('rate') || formType === 'agreement') ? (
                <div className="form-group">
                  <label htmlFor="number_of_body_cameras">
                    {formType === 'upsell' && hasUpsell('cameras') ? 'Additional Cameras'
                      : formType === 'upsell' ? 'Total Cameras'
                      : 'Cameras'}
                  </label>
                  <input type="number" id="number_of_body_cameras" name="number_of_body_cameras"
                    value={formData.number_of_body_cameras} onChange={handleInputChange} min="1"
                    readOnly={formType === 'upsell' && hasUpsell('rate') && !hasUpsell('cameras')}
                    required />
                </div>
              ) : null}
              {(hasUpsell('cameras') || hasUpsell('rate') || formType === 'agreement') ? (
                formType === 'upsell' && hasUpsell('rate') && currentSub ? (
                  <div className="form-group">
                    <label>Rate Discount (%)</label>
                    <div className="term-discount-input-wrap">
                      <PercentInput
                        label="Rate discount"
                        value={rateDiscountPct}
                        onChange={(pct) => {
                          setRateDiscountPct(pct);
                          setFormData(prev => ({ ...prev, fees: String(Math.ceil(currentSub.rate * (1 - pct / 100))) }));
                        }}
                        style={{ width: '60px' }}
                      />
                      <span className="term-discount-pct">%</span>
                    </div>
                    <span style={{ fontSize: '12px', color: rateDiscountPct > 0 ? '#22c55e' : '#718096', marginTop: '4px', display: 'block', fontWeight: 500 }}>
                      ${currentSub.rate}/device/mo → ${Math.ceil(currentSub.rate * (1 - rateDiscountPct / 100))}/device/mo
                      {rateDiscountPct > 0 && ` (save $${Math.floor(currentSub.rate * rateDiscountPct / 100)}/device/mo)`}
                    </span>
                  </div>
                ) : (
                  <div className="form-group">
                    <label htmlFor="fees">Rate / Device / Mo ($)</label>
                    <input type="number" id="fees" name="fees" value={formData.fees}
                      onChange={handleInputChange} min="1" required />
                  </div>
                )
              ) : null}
            </div>

            {/* Term selector — agreements and all change order types */}
            {formType === 'upsell' && hasUpsell('term') ? (
              <>
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="existing_term">Existing Term</label>
                    <select id="existing_term" value={existingTerm}
                      onChange={(e) => {
                        setExistingTerm(e.target.value);
                        const proposed = parseInt(formData.initial_term_months);
                        const existing = parseInt(e.target.value);
                        if (proposed <= existing) {
                          const next = existing === 12 ? '36' : '60';
                          setFormData(prev => ({ ...prev, initial_term_months: next }));
                        }
                      }}
                      required>
                      <option value="12">1 year</option>
                      <option value="36">3 years</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label htmlFor="initial_term_months">Proposed Term</label>
                    <select id="initial_term_months" name="initial_term_months" value={formData.initial_term_months}
                      onChange={handleInputChange} required>
                      {parseInt(existingTerm) < 36 && <option value="36">3 years</option>}
                      <option value="60">5 years</option>
                    </select>
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label>Show Terms</label>
                    <div className="pay-option-toggles">
                      {[{ value: '12', label: '1 Year' }, { value: '36', label: '3 Years' }, { value: '60', label: '5 Years' }]
                        .filter(t => parseInt(t.value) >= parseInt(existingTerm))
                        .map(t => (
                        <button key={t.value} type="button"
                          className={`pay-option-toggle ${visibleTermOptions.has(t.value) ? 'active' : ''}`}
                          onClick={() => {
                            setVisibleTermOptions(prev => {
                              const next = new Set(prev);
                              if (next.has(t.value)) { next.delete(t.value); } else { next.add(t.value); }
                              if (next.size === 0) next.add(existingTerm);
                              if (!next.has(formData.initial_term_months)) {
                                setFormData(fd => ({ ...fd, initial_term_months: Array.from(next).sort()[0] }));
                              }
                              return next;
                            });
                          }}
                        >{t.label}</button>
                      ))}
                    </div>
                  </div>
                  <div className="form-group">
                    <label>Show Payment Options</label>
                    <div className="pay-option-toggles">
                      {PAYMENT_SCHEDULES.map(p => (
                        <button key={p.value} type="button"
                          className={`pay-option-toggle ${visiblePayOptions.has(p.value) ? 'active' : ''}`}
                          onClick={() => {
                            setVisiblePayOptions(prev => {
                              const next = new Set(prev);
                              if (next.has(p.value)) { next.delete(p.value); } else { next.add(p.value); }
                              if (next.size === 0) next.add('upfront');
                              if (!next.has(formData.payment_schedule)) {
                                setFormData(fd => ({ ...fd, payment_schedule: Array.from(next)[0] }));
                              }
                              return next;
                            });
                          }}
                        >{p.shortLabel}</button>
                      ))}
                    </div>
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label>Term Discounts</label>
                    <div className="term-discount-editor">
                      {TERM_DISCOUNT_ROWS.map(({ key, label }) => (
                        <div key={key} className="term-discount-row">
                          <span className="term-discount-label">{label}</span>
                          <div className="term-discount-input-wrap">
                            <PercentInput
                              label={`${label} term discount`}
                              value={termDiscounts[key] * 100}
                              onChange={(pct) => setTermDiscounts(prev => ({ ...prev, [key]: pct / 100 }))}
                            />
                            <span className="term-discount-pct">%</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                  <div className="form-group">
                    <label>Payment Discounts</label>
                    <div className="term-discount-editor">
                      <div className="term-discount-row">
                        <span className="term-discount-label">Annual</span>
                        <div className="term-discount-input-wrap">
                          <PercentInput
                            label="Annual payment discount"
                            value={annualDiscount * 100}
                            onChange={(pct) => setAnnualDiscount(pct / 100)}
                          />
                          <span className="term-discount-pct">%</span>
                        </div>
                      </div>
                      <div className="term-discount-row">
                        <span className="term-discount-label">Upfront</span>
                        <div className="term-discount-input-wrap">
                          <PercentInput
                            label="Upfront payment discount"
                            value={upfrontDiscount * 100}
                            onChange={(pct) => setUpfrontDiscount(pct / 100)}
                          />
                          <span className="term-discount-pct">%</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </>
            ) : (
              <>
                <div className="form-row">
                  <div className="form-group">
                    <label>Show Terms</label>
                    <div className="pay-option-toggles">
                      {[{ value: '12', label: '1 Year' }, { value: '36', label: '3 Years' }, { value: '60', label: '5 Years' }].map(t => (
                        <button key={t.value} type="button"
                          className={`pay-option-toggle ${visibleTermOptions.has(t.value) ? 'active' : ''}`}
                          onClick={() => {
                            setVisibleTermOptions(prev => {
                              const next = new Set(prev);
                              if (next.has(t.value)) { next.delete(t.value); } else { next.add(t.value); }
                              if (next.size === 0) next.add('12');
                              if (!next.has(formData.initial_term_months)) {
                                setFormData(fd => ({ ...fd, initial_term_months: Array.from(next).sort()[0] }));
                              }
                              return next;
                            });
                          }}
                        >{t.label}</button>
                      ))}
                    </div>
                  </div>
                  <div className="form-group">
                    <label>Show Payment Options</label>
                    <div className="pay-option-toggles">
                      {PAYMENT_SCHEDULES.map(p => (
                        <button key={p.value} type="button"
                          className={`pay-option-toggle ${visiblePayOptions.has(p.value) ? 'active' : ''}`}
                          onClick={() => {
                            setVisiblePayOptions(prev => {
                              const next = new Set(prev);
                              if (next.has(p.value)) { next.delete(p.value); } else { next.add(p.value); }
                              if (next.size === 0) next.add('upfront');
                              if (!next.has(formData.payment_schedule)) {
                                setFormData(fd => ({ ...fd, payment_schedule: Array.from(next)[0] }));
                              }
                              return next;
                            });
                          }}
                        >{p.shortLabel}</button>
                      ))}
                    </div>
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>Term Discounts</label>
                    <div className="term-discount-editor">
                      {TERM_DISCOUNT_ROWS.map(({ key, label }) => (
                        <div key={key} className="term-discount-row">
                          <span className="term-discount-label">{label}</span>
                          <div className="term-discount-input-wrap">
                            <PercentInput
                              label={`${label} term discount`}
                              value={termDiscounts[key] * 100}
                              onChange={(pct) => setTermDiscounts(prev => ({ ...prev, [key]: pct / 100 }))}
                            />
                            <span className="term-discount-pct">%</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                  <div className="form-group">
                    <label>Payment Discounts</label>
                    <div className="term-discount-editor">
                      <div className="term-discount-row">
                        <span className="term-discount-label">Annual</span>
                        <div className="term-discount-input-wrap">
                          <PercentInput
                            label="Annual payment discount"
                            value={annualDiscount * 100}
                            onChange={(pct) => setAnnualDiscount(pct / 100)}
                          />
                          <span className="term-discount-pct">%</span>
                        </div>
                      </div>
                      <div className="term-discount-row">
                        <span className="term-discount-label">Upfront</span>
                        <div className="term-discount-input-wrap">
                          <PercentInput
                            label="Upfront payment discount"
                            value={upfrontDiscount * 100}
                            onChange={(pct) => setUpfrontDiscount(pct / 100)}
                          />
                          <span className="term-discount-pct">%</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </>
            )}

            {formType === 'agreement' && (
              <div className="form-group">
                <label>Implementation Fee (% of ACV)</label>
                <div className="impl-fee-selector">
                  {['10', '15', '20'].map(pct => (
                    <button
                      key={pct}
                      type="button"
                      className={`impl-fee-option ${formData.implementation_fee_pct === pct ? 'active' : ''}`}
                      onClick={() => setFormData(prev => ({ ...prev, implementation_fee_pct: pct }))}
                    >
                      {pct}%
                    </button>
                  ))}
                </div>
              </div>
            )}

            {formType === 'agreement' && (
              <label className="contingency-toggle">
                <input
                  type="checkbox"
                  checked={includeTrialClause}
                  onChange={(e) => setIncludeTrialClause(e.target.checked)}
                />
                <span>Contingency</span>
                {includeTrialClause && (
                  <span className="contingency-hint">10-day trial evaluation period included</span>
                )}
              </label>
            )}

            {formType === 'agreement' && (
              <label className="contingency-toggle">
                <input
                  type="checkbox"
                  checked={wifiOnly}
                  onChange={(e) => setWifiOnly(e.target.checked)}
                />
                <span>Wi-Fi only</span>
                {wifiOnly && (
                  <span className="contingency-hint">
                    livestream and in field uploads will be disabled — up to $20 off per camera
                  </span>
                )}
              </label>
            )}

            {formType === 'agreement' && (
              <label className="contingency-toggle">
                <input
                  type="checkbox"
                  checked={buyOut}
                  onChange={(e) => setBuyOut(e.target.checked)}
                />
                <span>Buy out</span>
                {buyOut && (
                  <span className="contingency-hint">
                    credits remaining term of current vendor contract — signed quote required at service start
                  </span>
                )}
              </label>
            )}

            <Button type="submit" size="lg" loading={loading}
              disabled={(!selectedCustomer && !isCustomCustomer) || !hasDeal || (isCustomCustomer && !formData.customer_name)}
              className="submit-button-layout">
              {formType === 'upsell' ? 'Generate Change Order' : 'Generate Agreement'}
            </Button>

            {docUrl && (
              <Alert type="success">
                {formType === 'upsell' ? 'Change order' : 'Agreement'} generated!{' '}
                <a href={docUrl} target="_blank" rel="noopener noreferrer">View Document</a>
              </Alert>
            )}
          </form>
        </div>

        {/* RIGHT: Live Pricing Card */}
        <div className="agreement-pricing-panel" ref={pricingRef}>
          {deal ? (
            <>
              <div className="pricing-header">
                <div className="pricing-header-row">
                  <h2>{formType === 'upsell'
                    ? `Change Order for ${formData.customer_name || 'Customer'}`
                    : `Pricing for ${formData.customer_name || 'Customer'}`}
                  </h2>
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <button
                      type="button"
                      className="export-pdf-btn"
                      onClick={exportToPdf}
                      disabled={exporting}
                    >
                      {exporting ? 'Exporting...' : 'Export PDF'}
                    </button>
                    <button
                      type="button"
                      className={`share-live-btn ${presentationMode ? 'active' : ''}`}
                      onClick={togglePresentationMode}
                    >
                      {presentationMode ? 'Exit' : 'Share Live'}
                    </button>
                  </div>
                </div>
                <div className="pricing-subtitle">
                  {formType === 'upsell'
                    ? [
                        hasUpsell('cameras') && `+${units} camera${units !== 1 ? 's' : ''}`,
                        hasUpsell('rate') && `$${effectiveRate}/device/mo`,
                        hasUpsell('term') && `${existingTerm === '12' ? '1yr' : '3yr'} \u2192 ${months === 36 ? '3yr' : '5yr'} term`,
                      ].filter(Boolean).join(' \u00b7 ')
                    : `${units} camera${units !== 1 ? 's' : ''} \u00b7 $${rate}/device/mo \u00b7 ${months} months`}
                </div>
              </div>

              {/* Big number: deal value */}
              <div className="pricing-hero">
                <div className="pricing-hero-amount">{deal.paymentLabel}</div>
                <div className="pricing-hero-schedule">
                  {deal.isFinancing ? 'Payment Agreement' : deal.isAnnual ? 'Annual' : 'Upfront'}
                  {(deal.termDiscountPct > 0 || deal.payDiscountPct > 0) && (
                    <span className="pricing-hero-discount">
                      {[
                        deal.termDiscountPct > 0 && `${(deal.termDiscountPct * 100).toFixed(0)}% term`,
                        deal.payDiscountPct > 0 && `${(deal.payDiscountPct * 100).toFixed(0)}% ${deal.isUpfront ? 'upfront' : 'annual'}`,
                      ].filter(Boolean).join(' + ')}
                    </span>
                  )}
                </div>
              </div>

              {/* Key metrics */}
              <div className="pricing-metrics">
                {formType === 'upsell' ? (
                  <>
                    <div className="pricing-metric">
                      <div className="metric-value">{hasUpsell('cameras') ? '+' : ''}${formatMoney(deal.monthly)}</div>
                      <div className="metric-label">{hasUpsell('cameras') ? 'Added MRR' : 'New MRR'}</div>
                    </div>
                    {hasUpsell('rate') && currentSub && rateDiscountPct > 0 && (
                      <div className="pricing-metric">
                        <div className="metric-value" style={{ color: '#22c55e' }}>
                          {rateDiscountPct}%
                        </div>
                        <div className="metric-label">Rate Discount</div>
                      </div>
                    )}
                    <div className="pricing-metric">
                      <div className="metric-value">${formatMoney(deal.tcv)}</div>
                      <div className="metric-label">TCV</div>
                    </div>
                    {hasUpsell('term') && deal.termDiscountPct > 0 && (
                      <div className="pricing-metric">
                        <div className="metric-value">{(deal.termDiscountPct * 100).toFixed(0)}%</div>
                        <div className="metric-label">Term Discount</div>
                      </div>
                    )}
                    {totalSavings > 0 && (
                      <div className="pricing-metric">
                        <div className="metric-value" style={{ color: '#22c55e' }}>${Math.ceil(totalSavings).toLocaleString()}</div>
                        <div className="metric-label">Total Savings</div>
                      </div>
                    )}
                  </>
                ) : (
                  <>
                    <div className="pricing-metric">
                      <div className="metric-value">${formatMoney(deal.monthly)}</div>
                      <div className="metric-label">Monthly</div>
                    </div>
                    <div className="pricing-metric">
                      <div className="metric-value">${formatMoney(deal.acv)}</div>
                      <div className="metric-label">ACV</div>
                    </div>
                    <div className="pricing-metric">
                      <div className="metric-value">${formatMoney(deal.tcv)}</div>
                      <div className="metric-label">TCV</div>
                    </div>
                    <div className="pricing-metric">
                      <div className="metric-value">${formatMoney(deal.onboarding)}</div>
                      <div className="metric-label">
                        Onboarding
                        {!presentationMode && (
                          <span
                            className="metric-info"
                            tabIndex={0}
                            data-tip="One-time setup fee — charged on the ACV of this proposal, so the term discount carries through to it. A prepayment discount does not: that comes off the contract total, not the per-camera rate. Hidden from the customer in Share Live."
                          >
                            i
                          </span>
                        )}
                      </div>
                    </div>
                  </>
                )}
              </div>

              {totalSavings > 0 && (
                <div className="pricing-savings-banner">
                  You save <strong>${Math.ceil(totalSavings).toLocaleString()}</strong>
                  {(() => {
                    const parts = [];
                    if (termSavings > 0) parts.push(`$${Math.ceil(termSavings).toLocaleString()} from term commitment`);
                    if (paySavings > 0) parts.push(`$${Math.ceil(paySavings).toLocaleString()} from upfront payment`);
                    if (rateSavings > 0) parts.push(`$${Math.ceil(rateSavings).toLocaleString()} from rate reduction`);
                    return parts.length > 1 ? ` (${parts.join(' + ')})` : parts.length === 1 ? ` ${parts[0].replace(/^\$[\d,]+\sfrom\s/, 'with ')}` : '';
                  })()}
                </div>
              )}

              {deal.isFinancing && (
                <div className="pricing-financing-note">
                  Flexible payment terms at ${formatMoney(deal.effectiveRate)}/device/mo.
                </div>
              )}

              {deal.isAnnual && (
                <div className="pricing-financing-note">
                  Billed annually at ${formatMoney(deal.paymentAmount)}/year.
                </div>
              )}

              {/* Upfront is the one row where the total is not rate x cameras x
                  months, so say why on the page rather than leave a rep to
                  explain a number that looks like it does not add up. */}
              {deal.isUpfront && deal.payDiscountPct > 0 && (
                <div className="pricing-financing-note">
                  ${formatMoney(deal.rateTcv)} over {months} months at ${formatMoney(deal.effectiveRate)}/device/mo,
                  less {(deal.payDiscountPct * 100).toFixed(0)}% (${formatMoney(deal.payDiscount)}) for paying upfront.
                </div>
              )}

              {/* Current vs New comparison — only when cameras or rate changes */}
              {formType === 'upsell' && (hasUpsell('cameras') || hasUpsell('rate')) && currentSub && currentSub.units > 0 && (
                <div className="pricing-comparison">
                  <h3>Current vs New</h3>
                  <div className="comparison-table">
                    <div className="comparison-header comparison-header--5col">
                      <span>Plan</span>
                      <span>Cameras</span>
                      <span>$/unit/mo</span>
                      <span>Monthly</span>
                      <span>ACV</span>
                    </div>
                    <div className="comparison-row comparison-row--5col">
                      <span className="comparison-label">Current</span>
                      <span>{currentSub.units}</span>
                      <span>${currentSub.rate}</span>
                      <span>${(currentSub.units * currentSub.rate).toLocaleString()}</span>
                      <span>${(currentSub.units * currentSub.rate * 12).toLocaleString()}</span>
                    </div>
                    <div className="comparison-row comparison-row--5col active">
                      <span className="comparison-label">
                        New
                        <span className="active-badge">Proposed</span>
                      </span>
                      <span>{hasUpsell('cameras') ? currentSub.units + units : currentSub.units}</span>
                      <span>${effectiveRate}</span>
                      <span>${(((hasUpsell('cameras') ? currentSub.units + units : currentSub.units) * effectiveRate)).toLocaleString()}</span>
                      <span>${(((hasUpsell('cameras') ? currentSub.units + units : currentSub.units) * effectiveRate * 12)).toLocaleString()}</span>
                    </div>
                  </div>
                </div>
              )}

              {/* Term comparison table */}
              <div className="pricing-comparison">
                <h3>Term Commitment</h3>
                <div className="comparison-table">
                  <div className="comparison-header comparison-header--5col">
                    <span>Term</span>
                    <span>Discount</span>
                    <span>$/unit/mo</span>
                    <span>TCV</span>
                    <span>You Save</span>
                  </div>
                  {['12', '36', '60'].filter(t => {
                    if (formType === 'upsell' && hasUpsell('term')) return visibleTermOptions.has(t) && parseInt(t) >= parseInt(existingTerm);
                    return visibleTermOptions.has(t) && parseInt(t) >= parseInt(effectiveTermBaseline);
                  }).map(t => {
                    const termMonths = parseInt(t);
                    const termUnits = formType === 'upsell' && hasUpsell('term') ? (currentSub?.units || units) : units;
                    const termRate = formType === 'upsell' && hasUpsell('term') ? (currentSub?.rate || rate) : rate;
                    const d = computeDeal(termUnits, termRate, termMonths, formData.payment_schedule, effectivePayBaseline, comparisonTermBaseline, implFee, termDiscounts, upfrontDiscount, annualDiscount);
                    const isExisting = formType === 'upsell' && hasUpsell('term') && t === existingTerm;
                    const isActive = String(months) === t;
                    const savings = d.baseTcv - d.tcv;
                    // The row's own rate, not one backed out of its TCV: on a
                    // prepaid deal the TCV carries a pay-timing cut the camera
                    // price does not, so dividing it back out understated the rate.
                    const unitCost = d.effectiveRate;
                    const termLabel = t === '12' ? '1 Year' : t === '36' ? '3 Years' : '5 Years';
                    return (
                      <div
                        key={t}
                        className={`comparison-row comparison-row--5col ${isActive ? 'active' : ''} ${d.termDiscountPct > 0 ? 'has-discount' : ''}`}
                        onClick={() => setFormData(prev => ({ ...prev, initial_term_months: t }))}
                      >
                        <span className="comparison-label">
                          {termLabel}
                          {isExisting && <span className="existing-badge">Existing</span>}
                          {isActive && !isExisting && <span className="active-badge">Proposed</span>}
                        </span>
                        <span className="comparison-discount">
                          {d.termDiscountPct > 0 ? `${(d.termDiscountPct * 100).toFixed(0)}%` : '—'}
                        </span>
                        <span className="comparison-unit-cost">${unitCost.toFixed(2)}</span>
                        <span className="comparison-tcv">${formatMoney(d.tcv)}</span>
                        <span className={`comparison-savings ${savings > 0 ? 'has-savings' : ''}`}>
                          {savings > 0 ? `$${formatMoney(savings)}` : '—'}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Payment options — 5-col matching term commitment */}
              <div className="pricing-comparison">
                <h3>Payment Options</h3>
                <div className="comparison-table">
                  <div className="comparison-header comparison-header--5col">
                    <span>Option</span>
                    <span>Discount</span>
                    <span>$/unit/mo</span>
                    <span>Total</span>
                    <span>You Save</span>
                  </div>
                  {SCHEDULE_ORDER.filter(s => visiblePayOptions.has(s)).map(sched => {
                    const d = computeDeal(units, effectiveRate, months, sched, effectivePayBaseline, dealTermBaseline, implFee, termDiscounts, upfrontDiscount, annualDiscount);
                    // Compare against annual (no pay discount) as baseline for savings
                    const baseline = computeDeal(units, effectiveRate, months, 'annual', effectivePayBaseline, dealTermBaseline, implFee, termDiscounts, upfrontDiscount, annualDiscount);
                    const isActive = sched === formData.payment_schedule;
                    // Identical down the column by design -- the payment method
                    // changes what the term costs, not what a camera costs.
                    const unitCost = d.effectiveRate;
                    const savings = baseline.tcv - d.tcv;
                    return (
                      <div
                        key={sched}
                        className={`comparison-row comparison-row--5col ${isActive ? 'active' : ''} ${d.payDiscountPct > 0 ? 'has-discount' : ''}`}
                        onClick={() => setFormData(prev => ({ ...prev, payment_schedule: sched }))}
                      >
                        <span className="comparison-label">
                          {PAYMENT_SCHEDULES.find(p => p.value === sched)?.label}
                          {isActive && <span className="active-badge">Current</span>}
                        </span>
                        <span className="comparison-discount">
                          {d.payDiscountPct > 0 ? `${(d.payDiscountPct * 100).toFixed(0)}%` : '—'}
                        </span>
                        <span className="comparison-unit-cost">${unitCost.toFixed(2)}</span>
                        <span className="comparison-tcv">${formatMoney(d.tcv)}</span>
                        <span className={`comparison-savings ${savings > 0 ? 'has-savings' : ''}`}>
                          {sched === 'financing' ? 'Flexible terms' : savings > 0 ? `$${formatMoney(savings)}` : '—'}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Total */}
              <div className="pricing-total">
                <div className="pricing-total-row">
                  <span>{formType === 'upsell' ? 'Total' : 'Total Contract + Onboarding'}</span>
                  <strong>${formType === 'upsell' ? formatMoney(deal.tcv) : formatMoney(deal.tcv + deal.onboarding)}</strong>
                </div>
              </div>

              {/* Package feature table (agreements only) */}
              {formType === 'agreement' && (
                <div className="package-table-section">
                  <h3>{PACKAGES.find(p => p.value === formData.package_tier)?.label} Package</h3>
                  <table className="package-table">
                    <tbody>
                      {PACKAGE_FEATURES.map(f => (
                        <tr key={f.feature}>
                          <td className="feature-name">{f.feature}</td>
                          <td className="feature-value">
                            {f.feature === 'Pooled Livestream'
                              ? `${livestreamAllowance(Number(formData.number_of_body_cameras))} / month`
                              : f.command}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          ) : (
            <div className="pricing-empty">
              <div className="pricing-empty-icon">$</div>
              <p>{formType === 'upsell' ? 'Select a customer and change type to see live pricing' : 'Enter cameras and rate to see live pricing'}</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AgreementGenerator;
